import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { KeyValuePair } from '@moderepo/mode-internal-apis';
import { KVPairsTable, KVPairsTableDataItem, KVPairsTableProps, capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { localizeKeyValuePairData } from '../containerUtils';
import { DeviceKVPreviewCompOptions, isDeviceKVPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { useSelectAllDeviceKVStores } from '../../utils/dataStateHooks';



/**
 * Because this is a container, not a page, it doesn't and shouldn't have access to the route params. Therefore, the page
 * that display this container must provide these information.
 */
interface DeviceKVPairsListingContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly deviceId: number;
}


/**
 * This is a container (SMART COMPONENT) used for displaying a list of KV Pairs. Since this is a container, it will have
 * little to no UI. All the UI will be components combined together to build the content.
 */
export const DeviceKVPairsListingContainer = (props: DeviceKVPairsListingContainerProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [selectedKVPairKey, setSelectedKVPairKey] = useState<string | undefined>();
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const kvPairs = useSelectAllDeviceKVStores(props.deviceId);



    const onKVPairSelected = useCallback((key: string | undefined) => {
        setSelectedKVPairKey(key);
        const options: DeviceKVPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DEVICE_KV_PREVIEW,
            compProps: {
                data: { projectId: props.projectId, key, deviceId: props.deviceId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.deviceId, uiDispatch]);



    // This useEffect is used for checking the URL to see if there is any selectedEntity and if that selected entity is a KY Pair key.
    // if it is, automatically set it as the setSelectedKVPairKey
    useEffect(() => {
        if (isDeviceKVPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data?.key !== undefined) {
            setSelectedKVPairKey(slideOutPanelOptions.compProps.data.key);
        } else {
            setSelectedKVPairKey(undefined);
        }
    }, [slideOutPanelOptions]);


    /**
     * Localize the KV Pairs data before passing it to the KVPairTableComp for display
     */
    const localizedKVPairsData = useMemo((): readonly KVPairsTableDataItem[] | undefined => {
        return localizeKeyValuePairData(kvPairs, trans, selectedKVPairKey);
    }, [trans, selectedKVPairKey, kvPairs]);



    const onCreateNewKVPairClicked = useCallback(() => {
        onKVPairSelected(undefined);
    }, [onKVPairSelected]);


    // Create table settings and cache it
    const tableFieldsSettings = useMemo((): KVPairsTableProps['fieldsSettings'] => {
        return {
            fields: {
                key: {
                    dataItemProp: 'key',
                    label       : capitalizeAll(trans('label.kv_pair_key')),
                },
                value: {
                    dataItemProp: 'value',
                    label       : capitalizeAll(trans('label.value')),
                },
                modificationTime: {
                    dataItemProp: 'modificationTime',
                    label       : capitalizeAll(trans('label.updated_date')),
                },
            },
        };
    }, [trans]);


    return (
        <>
            {project && (
                <KVPairsTable
                    className={clsx(props.className)}
                    listData={localizedKVPairsData}
                    title={capitalizeAll(trans('label.kv_pair', { count: 2 }))}
                    icon="fas fa-database"
                    onListItemSelected={(kvPair: KeyValuePair) => {
                        onKVPairSelected(kvPair.key);
                    }}
                    customPrimaryActions={[{
                        label  : capitalizeAll(trans('label.add')),
                        icon   : 'add',
                        variant: 'contained',
                        color  : 'primary',
                        onClick: onCreateNewKVPairClicked,
                    }]}
                    fieldsSettings={tableFieldsSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.kv_pair', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.kv_pair', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {!project && (
                <CircularProgress />
            )}
        </>
    );
};
